/**
 * 星球活动的读与写（见 ./events 的文件头）。
 * - 活动列表公开、不分账号；这只宠物告诉过的活动分账号、分宠物（切宠物就是另一条查询，按钮状态不串）。
 * - 当前宠物：家庭上下文里选中的那只；演示世界的家庭上下文没有当前宠物，用小窝快照里的那只（同通讯器页、TA 的朋友）。
 * - 告诉 TA / 不告诉了：成功后把答回来的那一条直接写进这只宠物的记录，再让它按规则重读一次。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlanetEventTell, PlanetEventTellList } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { useActiveHome, useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { eventTellsKey, planetEventsKey, sourceOf } from "./events";

export function usePlanetEventList() {
  const { communicator } = useServices();
  return useQuery({ queryKey: planetEventsKey, queryFn: ({ signal }) => communicator.planetEvents(signal), retry: false, staleTime: 60_000 });
}

/** 当前账号与当前宠物（切宠物时跟着换）。 */
export function useEventPet(): { userId: string | null; petId: string | null; petName: string | null } {
  const household = useOptionalCurrentHousehold();
  const home = useActiveHome();
  const petId = household?.pet?.pet_id ?? home.data?.pet.pet_id ?? null;
  const petName = household?.pet?.name ?? home.data?.pet.name ?? null;
  return { userId: household?.userId ?? null, petId, petName };
}

export function usePlanetEventTells(userId: string | null, petId: string | null) {
  const { communicator } = useServices();
  return useQuery({
    queryKey: eventTellsKey(userId, petId),
    queryFn: ({ signal }) => communicator.eventTells(petId!, signal),
    enabled: Boolean(petId) && (env.dataMode === "fixture" || Boolean(userId)),
    retry: false,
  });
}

/** 把答回来的一条写进这只宠物的记录（同一活动、同一来源的有就换、没有就加），不等重读；另一来源的那一行不动。 */
function merge(list: PlanetEventTellList | undefined, tell: PlanetEventTell): PlanetEventTellList {
  const others = (list?.tells ?? []).filter((item) => !(item.event_id === tell.event_id && sourceOf(item) === sourceOf(tell)));
  return { tells: [tell, ...others] };
}

/** 告诉 TA / 不告诉了。只对当前这只宠物；petId 为空时不发。 */
export function useEventTellActions(userId: string | null, petId: string | null) {
  const { communicator } = useServices();
  const queryClient = useQueryClient();
  const key = eventTellsKey(userId, petId);
  // 写回点按钮时那只宠物的记录：key 和 petId 出自同一次渲染（按钮所在的卡片按宠物重建，切宠物不会串到另一只）。
  const settle = (tell: PlanetEventTell) => {
    queryClient.setQueryData<PlanetEventTellList>(key, (list) => merge(list, tell));
    void queryClient.invalidateQueries({ queryKey: key });
  };
  const tell = useMutation({
    mutationFn: (eventId: string) => communicator.tellEvent(petId!, eventId),
    onSuccess: settle,
  });
  const withdraw = useMutation({
    // 家人告诉的那一行不带 source（后端缺省就是 owner）；撤 TA 自己听说的那一行带 source=heard
    mutationFn: ({ eventId, source }: { eventId: string; source: "owner" | "heard" }) =>
      source === "heard" ? communicator.withdrawEventTell(petId!, eventId, "heard") : communicator.withdrawEventTell(petId!, eventId),
    onSuccess: settle,
  });
  return { tell, withdraw, refresh: () => void queryClient.invalidateQueries({ queryKey: key }) };
}
